import React from "react";
import { MousePointerClick } from "lucide-react";

interface ClickBadgeProps {
  clickCount: number | undefined | null;
  /** Tighter padding/text for the compact card row. */
  compact?: boolean;
  /** Render even when count is zero (default false → hide unrecorded cards). */
  showZero?: boolean;
}

function formatClicks(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
}

/**
 * Tiny chip showing total recorded card taps. Designed to sit alongside the
 * Active / Expires chips on a Happening now card row. Hidden by default when
 * count is 0 so cards without telemetry aren't mislabeled.
 */
export const ClickBadge: React.FC<ClickBadgeProps> = ({
  clickCount,
  compact = false,
  showZero = false,
}) => {
  const n = typeof clickCount === "number" && clickCount > 0 ? clickCount : 0;
  if (n === 0 && !showZero) return null;

  const padding = compact ? "px-1.5 py-0.5" : "px-2 py-0.5";
  const textSize = compact ? "text-[9px]" : "text-[10px]";
  const iconSize = compact ? "h-2.5 w-2.5" : "h-3 w-3";

  return (
    <span
      title={`${n.toLocaleString()} taps recorded`}
      className={`inline-flex items-center gap-1 rounded-${compact ? "md" : "lg"} bg-[#EEF2FF] ${padding} ${textSize} font-extrabold uppercase text-[#4338CA]`}
    >
      <MousePointerClick className={iconSize} aria-hidden />
      {formatClicks(n)} {n === 1 ? "tap" : "taps"}
    </span>
  );
};
